import { useState } from 'react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import type { TLessonBase, TLessonCategoryBase } from '@onesource/schemas';
import { ModuleLessonGroup } from './module-lesson-group';
import { PiBookOpenLight } from 'react-icons/pi';
import { FiMinus, FiPlus } from 'react-icons/fi';
import { BsChevronRight } from 'react-icons/bs';
import { useTheme } from '@mui/material';
import { useGetByDevice } from '@onesource/components';
import { useNavigate } from '@tanstack/react-router';
import { getLessonColorScheme } from './lesson-color-scheme';

interface ModuleSectionCardProps {
  section: TLessonBase;
  index: number;
  subLessons: TLessonBase[];
  categories: TLessonCategoryBase[];
  parentSpaceId: string;
  moduleId: string;
  expandedSectionId?: string;
  showSectionLabel?: boolean;
  showLessonLabel?: boolean;
}

/**
 * Module Section Card Component
 * Accordion-style card showing a section with expandable lessons.
 * If the section has no sub-lessons, clicking navigates directly to the lesson detail page.
 */
export const ModuleSectionCard = ({
  section,
  index,
  subLessons,
  categories,
  parentSpaceId,
  moduleId,
  expandedSectionId,
  showSectionLabel = true,
  showLessonLabel = true,
}: ModuleSectionCardProps) => {
  const [expanded, setExpanded] = useState(expandedSectionId ? section.id === expandedSectionId : index === 0); // Match expandedSectionId if provided, otherwise first section expanded by default
  const { isMobile } = useGetByDevice();
  const navigate = useNavigate();

  const theme = useTheme();
  const hasSubLessons = subLessons.length > 0;

  const handleClick = () => {
    if (hasSubLessons) {
      setExpanded(!expanded);
    } else {
      navigate({
        to: `/community-hub/spaces/${parentSpaceId}/modules/${moduleId}/lessons/${section.id}`,
      });
    }
  };

  // Group sub-lessons by category
  const sortedCategories = [...categories].sort((a, b) => a.position - b.position);
  const categorizedLessons = new Map<string | null, TLessonBase[]>();

  // Initialize with sorted categories
  for (const cat of sortedCategories) {
    categorizedLessons.set(cat.id, []);
  }
  categorizedLessons.set(null, []); // Uncategorized

  // Group lessons
  for (const lesson of subLessons) {
    const catId = lesson.categoryId || null;
    if (!categorizedLessons.has(catId)) {
      categorizedLessons.set(null, [...(categorizedLessons.get(null) || []), lesson]);
    } else {
      categorizedLessons.get(catId)!.push(lesson);
    }
  }

  // Sort lessons within each category by position
  categorizedLessons.forEach((lessons, key) => {
    categorizedLessons.set(
      key,
      lessons.sort((a, b) => a.position - b.position),
    );
  });

  // Build render order: categories first (by position), then uncategorized
  const renderOrder: Array<{ categoryId: string | null; name: string; lessons: TLessonBase[] }> = [];
  for (const cat of sortedCategories) {
    const lessons = categorizedLessons.get(cat.id) || [];
    if (lessons.length > 0) {
      renderOrder.push({ categoryId: cat.id, name: cat.name, lessons });
    }
  }
  const uncategorized = categorizedLessons.get(null) || [];
  if (uncategorized.length > 0 && sortedCategories.length === 0) {
    // Only add uncategorized if no categories exist
    renderOrder.push({ categoryId: null, name: '', lessons: uncategorized });
  }

  const sectionColors = getLessonColorScheme(section.colorScheme);
  const sectionTitle = section.title.length > 23 && isMobile ? `${section.title.slice(0, 23)}...` : section.title;

  return (
    <Box
      sx={{
        borderRadius: '10px',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        bgcolor: 'white',
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          borderRadius: 'inherit',
          boxShadow:
            'inset 0px 0.596px 0px 0px rgba(255, 255, 255, 0.7), inset 0px -2.391px 2.383px 0px rgba(255, 100, 130, 0.2)',
        },
      }}
    >
      {/* Section Header */}
      <Box
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: { xs: 60, md: 60 },
          cursor: 'pointer',
          transition: 'background-color 0.3s ease',
        }}
      >
        {/* Section Icon */}
        <Box
          sx={{
            width: 62,
            height: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: hasSubLessons && expanded ? theme.palette.common.white : sectionColors.bgcolor,
            flexShrink: 0,
            transition: 'background-color 0.3s ease',
          }}
        >
          <PiBookOpenLight
            size={32}
            color={hasSubLessons && expanded ? theme.palette.primary.main : sectionColors.iconColor}
            style={{ transition: 'color 0.3s ease' }}
          />
        </Box>

        {/* Header Bar */}
        <Box
          sx={{
            flex: 1,
            bgcolor: hasSubLessons && expanded ? '#fffbf8' : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: '16px',
            py: '10px',
            borderRadius: hasSubLessons && expanded ? '2px 10px 10px 2px' : '0',
            transition: 'background-color 0.3s ease, border-radius 0.3s ease',
          }}
        >
          {/* Section Title */}
          <Box sx={{ minWidth: 0 }}>
            {showSectionLabel && (
              <Typography
                sx={{
                  fontFamily: '"Fraunces", serif',
                  fontWeight: 700,
                  fontSize: 12,
                  lineHeight: '19px',
                  color: 'black',
                }}
              >
                Section {index}:
              </Typography>
            )}
            <Typography
              sx={{
                fontFamily: '"Figtree", sans-serif',
                fontWeight: !showSectionLabel ? 700 : 400,
                fontSize: 12,
                color: 'black',
              }}
            >
              {sectionTitle}
            </Typography>
          </Box>

          {/* Expand/Collapse or Open Button */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Figtree", sans-serif',
                fontWeight: 700,
                fontSize: 12,
                lineHeight: '17px',
                color: '#ff073b',
              }}
            >
              {hasSubLessons
                ? isMobile
                  ? expanded
                    ? 'Collapse'
                    : 'Expand'
                  : expanded
                    ? 'Collapse Lessons'
                    : 'Expand To See Lessons'
                : 'Open'}
            </Typography>
            <Box
              component="span"
              sx={{
                color: '#ff073b',
                fontSize: 12,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {hasSubLessons ? expanded ? <FiMinus size={12} /> : <FiPlus size={12} /> : <BsChevronRight size={12} />}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Expanded Content - only for sections with sub-lessons */}
      {hasSubLessons && (
        <Collapse
          in={expanded}
          timeout="auto"
          easing={{ enter: 'cubic-bezier(0.4, 0, 0.2, 1)', exit: 'cubic-bezier(0.4, 0, 0.2, 1)' }}
        >
          <Box
            sx={{
              mt: 3,
              bgcolor: 'white',
              px: { xs: 2, md: 3, lg: '78px' },
              pb: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '18px',
              opacity: expanded ? 1 : 0,
              transform: expanded ? 'translateY(0)' : 'translateY(-8px)',
              transition: 'opacity 0.35s ease, transform 0.35s ease',
            }}
          >
            {renderOrder.map((group, groupIndex) => (
              <ModuleLessonGroup
                key={group.categoryId || `uncategorized-${groupIndex}`}
                categoryName={group.name}
                lessons={group.lessons}
                parentSpaceId={parentSpaceId}
                moduleId={moduleId}
                sectionId={section.id}
                showLessonLabel={showLessonLabel}
              />
            ))}

            {renderOrder.length === 0 && (
              <Typography
                sx={{
                  fontFamily: '"Figtree", sans-serif',
                  fontWeight: 400,
                  fontSize: 14,
                  lineHeight: '19px',
                  color: 'rgba(0, 0, 0, 0.6)',
                }}
              >
                No lessons available in this section.
              </Typography>
            )}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};
